import { moveById } from "./moves";
import type { ExerciseId } from "./types";

/**
 * Hindi for the player and the session overview, and nothing else (ADR-055). The move names are
 * P1's `hi` (`exerciseMeta.ts::HI` and `moves.ts`); this file adds the player's own words, keyed
 * by the English a screen would show, a first-line cue for the moves a parent's sessions use, and
 * the fixed cue phrases the Steady fill writes. Anything without an entry stays in English — the
 * player never shows a blank.
 *
 * The set was checked against EXAMPLE-DATA's "second design pass" list; the owner reads the rest
 * before it ships. Everyday Hindi, not the formal register: "रुकें", not "विराम".
 */

export const UI_HI = {
  Start: "शुरू करें",
  "Start now": "अभी शुरू करें",
  Done: "हो गया",
  Rest: "आराम",
  Next: "अगला",
  Stop: "रुकें",
  Pause: "रोकें",
  Resume: "फिर से शुरू",
  "Skip rest": "आराम छोड़ें",
  Previous: "पिछला",
  "How to do this move": "कैसे करें",
  More: "और",
  Sound: "आवाज़",
  On: "चालू",
  Off: "बंद",
  "minutes left": "मिनट बाकी",
  "moves done": "हो चुके",
  minutes: "मिनट",
  moves: "कसरतें",
  round: "राउंड",
  "Stand up slowly": "धीरे से खड़े हों",
  "Sit a moment, then stand with a hand on the counter until you feel steady.":
    "थोड़ा बैठें, फिर काउंटर पर एक हाथ रखकर खड़े हों, जब तक संभल न जाएँ।",
  Breathe: "साँस लें",
  "Sit down and breathe.": "बैठ जाएँ और आराम से साँस लें।",
  "Well done": "शाबाश",
  "Session complete": "आज की कसरत पूरी",
  "active min": "मिनट कसरत",
  "How was it?": "कैसा रहा?",
  "Too easy": "बहुत आसान",
  "About right": "ठीक था",
  "Too hard": "बहुत मुश्किल",
  "Anything hurt?": "कहीं दर्द हुआ?",
  "Could you talk?": "क्या आप बात कर पा रहे थे?",
  "Could talk easily": "आराम से बात कर पा रहे थे",
  "Could talk, not sing": "बात कर पा रहे थे, गा नहीं",
  "Hard to talk": "बात करना मुश्किल था",
  "How much? 0 is none, 10 the worst you can imagine.": "कितना? 0 यानी बिल्कुल नहीं, 10 यानी सबसे ज़्यादा।",
  "Pick a joint and Kaya asks how much, 0 to 10. Nothing picked means nothing hurt.":
    "जहाँ दर्द हुआ, उसे चुनें। कुछ न चुनें तो मतलब कहीं दर्द नहीं हुआ।",
  Neck: "गर्दन",
  Shoulder: "कंधा",
  Back: "कमर",
  Hip: "कूल्हा",
  Knee: "घुटना",
  "Ankle or foot": "टखना या पैर",
  Wrist: "कलाई",
  Save: "सेव करें",
  Skip: "छोड़ें",
  Cancel: "रद्द करें",
  "Keep going": "जारी रखें",
  "Stop the session?": "कसरत रोकें?",
  "Moves you have done stay ticked.": "जो हो चुका, वह गिना रहेगा।",
  "left side": "बायाँ तरफ़",
  "right side": "दायाँ तरफ़",
  "each side": "हर तरफ़",
  reps: "बार",
  seconds: "सेकंड",
  "get set": "तैयार हो जाएँ",
  paused: "रुका हुआ",
  "rest left": "आराम बाकी",
  "Timed moves and rests go on by themselves. Tap Done when your reps are finished.":
    "समय वाली कसरतें और आराम अपने आप आगे बढ़ते हैं। गिनती पूरी हो तो “हो गया” दबाएँ।",
  "Nothing to run today": "आज कोई कसरत नहीं",
  /* the Steady templates' names (templates.ts) */
  "Strength & balance": "ताक़त और संतुलन",
  "Gentle cardio & mobility": "हल्की कसरत और लचीलापन",
  "Chair session": "कुर्सी पर कसरत",
  "Morning joints": "सुबह जोड़ों की कसरत",
  "Walk day": "सैर का दिन",
  /* the cue phrases the Steady fill writes (templates.ts, programme.ts::rungCue) */
  "hands allowed": "हाथों के सहारे",
  "with hands": "हाथों के सहारे",
  sitting: "बैठकर",
  "both hands on the counter": "दोनों हाथ काउंटर पर",
  "one hand on the counter": "एक हाथ काउंटर पर",
  "fingertips on the counter": "उँगलियाँ काउंटर पर",
  "no hands, the counter in reach": "बिना हाथ के, काउंटर पास में",
  "no hands; turn your head slowly, the counter in reach": "बिना हाथ के; सिर धीरे से घुमाएँ, काउंटर पास में",
  "a hand on the counter": "एक हाथ काउंटर पर",
  "the counter in reach": "काउंटर पास में",
  "both hands on the wall": "दोनों हाथ दीवार पर",
  "one hand on the wall": "एक हाथ दीवार पर",
  "fingertips on the wall": "उँगलियाँ दीवार पर",
  "no hands, the wall in reach": "बिना हाथ के, दीवार पास में",
  "no hands; turn your head slowly, the wall in reach": "बिना हाथ के; सिर धीरे से घुमाएँ, दीवार पास में",
  "a hand on the wall": "एक हाथ दीवार पर",
  "the wall in reach": "दीवार पास में",
  /* Morning yoga & breathing (ADR-067) */
  "Morning yoga & breathing": "सुबह योग और प्राणायाम",
} as const;

export type UiWord = keyof typeof UI_HI;

export function isUiWord(s: string): s is UiWord {
  return Object.hasOwn(UI_HI, s);
}

/** The player's words in its language; English when Hindi has no entry, never a blank. */
export function tr(lang: "en" | "hi", en: string): string {
  return lang === "hi" && isUiWord(en) ? UI_HI[en] : en;
}

/**
 * A first-line cue in Hindi for the moves a parent's sessions use (the Steady templates and their
 * easier and harder steps); every other move keeps its English `how`. The meaning follows the
 * move's first sentence in `moves.ts`.
 */
export const CUE_HI: Partial<Record<ExerciseId, string>> = {
  sitstand: "आगे झुकें, नाक पंजों के ऊपर, एड़ियों से उठें।",
  minisquat: "काउंटर पकड़कर घुटने थोड़ा मोड़ें, कूल्हे पीछे ले जाएँ, फिर सीधे खड़े हों।",
  kneeext: "सीधे बैठें, एक पैर घुटने से सीधा करें, रुकें, फिर धीरे से नीचे लाएँ।",
  sideleg: "काउंटर पकड़कर सीधे खड़े हों, एक पैर बगल में उठाएँ, शरीर सीधा रखें।",
  kneecurl: "काउंटर पकड़कर खड़े हों, एड़ी को पीछे कूल्हे की ओर मोड़ें।",
  calfraise: "काउंटर पकड़कर पंजों पर ऊपर उठें, फिर धीरे से एड़ियाँ नीचे लाएँ।",
  toeraise: "काउंटर पकड़कर एड़ियों पर टिकें, पंजे ऊपर उठाएँ।",
  armcurl: "बैठकर हाथ में बोतल लें, कोहनी से मोड़कर कंधे तक लाएँ।",
  row: "बैठकर कोहनियाँ पीछे खींचें, कंधे की हड्डियाँ आपस में दबाएँ।",
  wallpush: "दीवार पर हाथ रखें, छाती दीवार की ओर ले जाएँ, फिर धकेलकर वापस आएँ।",
  tandemstand: "काउंटर पकड़कर एक पैर दूसरे के ठीक आगे रखें और रुकें।",
  semitandem: "काउंटर पकड़कर एक पैर आधा आगे रखें और रुकें।",
  onelegstand: "काउंटर पकड़कर एक पैर ज़मीन से थोड़ा उठाएँ और रुकें।",
  sidewalk: "काउंटर के साथ बगल में कदम रखते हुए चलें।",
  tandemwalk: "काउंटर के साथ एड़ी से पंजा मिलाकर आगे चलें।",
  backwalk: "काउंटर पकड़कर धीरे-धीरे पीछे की ओर चलें।",
  calfstretch: "दीवार पर हाथ रखें, एक पैर पीछे, एड़ी ज़मीन पर दबाएँ।",
  hamchair: "कुर्सी के किनारे बैठें, एक पैर सीधा आगे रखें, सीधी पीठ से थोड़ा आगे झुकें।",
  breathing: "आराम से बैठें, नाक से धीरे साँस लें, मुँह से धीरे छोड़ें।",
  neckturn: "सीधे बैठें, सिर धीरे से एक कंधे की ओर घुमाएँ, फिर दूसरी ओर।",
  shroll: "कंधों को धीरे-धीरे पीछे की ओर गोल घुमाएँ।",
  ankle: "बैठकर पैर थोड़ा उठाएँ, टखने को गोल घुमाएँ।",
  seatmarch: "सीधे बैठें, बारी-बारी से घुटने ऊपर उठाएँ।",
  trunkturn: "हाथ छाती पर बाँधें, कंधे धीरे से एक तरफ़ घुमाएँ, कूल्हे और घुटने सामने रखें।",
  backext: "कुर्सी के आगे बैठें, हाथ कमर पर, छाती उठाएँ और हल्का पीछे झुकें।",
  march: "एक जगह पर कदमताल करें, घुटने ऊपर, हाथ हिलाते हुए।",
  steptouch: "एक कदम बगल में रखें, दूसरा पैर उसके पास छुआएँ, फिर दूसरी तरफ़।",
  heeldig: "सीधे बैठें, बारी-बारी से एड़ी आगे ज़मीन पर टिकाएँ।",
  seatpunch: "सीधे बैठें, बारी-बारी से हाथ आगे मुक्के की तरह बढ़ाएँ।",
  armraise: "सीधे बैठें, साँस लेते हुए दोनों हाथ ऊपर उठाएँ, छोड़ते हुए नीचे लाएँ।",
  wallslide: "दीवार की ओर मुँह करें, बाँहें दीवार पर रखकर ऊपर सरकाएँ, फिर नीचे।",
  weightshift: "काउंटर पकड़ें, वज़न धीरे से एक पैर पर लाएँ, रुकें, फिर दूसरे पर।",
  tadasana: "एक हाथ काउंटर पर, साँस लेते हुए दूसरा हाथ ऊपर और पंजों पर उठें।",
  standhipflex: "काउंटर पकड़ें, एक पैर पीछे, कूल्हे के आगे खिंचाव महसूस करें।",
  press: "सीधे बैठें, बोतलें कंधों पर, साँस छोड़ते हुए ऊपर उठाएँ, धीरे से नीचे लाएँ।",
  towelsqueeze: "तौलिया लपेटकर दोनों हाथों से ज़ोर से दबाएँ और निचोड़ें, साँस चलती रहे।",
  chintuck: "सीधे बैठें, सामने देखें, ठोड़ी सीधे पीछे खींचें, रुकें, छोड़ें।",
  sidebendchair: "सीधे बैठें, एक हाथ कुर्सी पर, दूसरा हाथ ऊपर से बगल में झुकाएँ।",
  anulom: "सीधे बैठें, एक नथुने से साँस लें, दूसरे से छोड़ें, धीरे और शांत।",
  bhramari: "सीधे बैठें, आँखें बंद, नाक से साँस लें, छोड़ते हुए धीमे से गुनगुनाएँ।",
  quadset: "पैर सीधे रखें, घुटने के नीचे तौलिया, जाँघ कसकर घुटना तौलिये पर दबाएँ।",
  heelslide: "पीठ के बल लेटें, घुटने मुड़े, एक एड़ी आगे सरकाकर पैर सीधा करें, फिर वापस।",
  pelvictilt: "पीठ के बल लेटें, घुटने मुड़े, कमर धीरे से बिस्तर पर दबाएँ, रुकें, छोड़ें।",
  kneechest: "पीठ के बल लेटें, एक घुटना छाती से लगाएँ, दूसरा पैर सीधा।",
  bridge: "पीठ के बल लेटें, घुटने मुड़े, एड़ियों से ज़ोर देकर कूल्हे ऊपर उठाएँ।",
  walk: "इतनी चाल से चलें कि बात कर सकें, गा न सकें, सीधे समतल रास्ते पर।",
  wallsit: "पीठ दीवार से सटाकर थोड़ा नीचे सरकें, ज़ोर से गिनती करते हुए रुकें।",
  seatfig4: "सीधे बैठें, एक टखना दूसरे घुटने पर, सीधी पीठ से थोड़ा आगे झुकें।",
  chairtadasana: "कुर्सी के आगे सीधे बैठें, साँस लेते हुए दोनों हाथ ऊपर, छोड़ते हुए नीचे।",
  foldchair: "पैर खोलकर बैठें, साँस छोड़ते हुए कूल्हों से आगे झुकें, जितना आराम से हो; धीरे से ऊपर आएँ।",
  catcowchair: "हाथ घुटनों पर, साँस लेते हुए छाती उठाएँ, छोड़ते हुए पीठ गोल करें और ठोड़ी अंदर।",
  kapalbhati: "सीधे बैठें, नाक से झटके से साँस छोड़ें, पेट अंदर; साँस अपने आप आने दें। धीरे-धीरे।",
};

/** A move's Hindi name, or null. */
export function hiName(id: ExerciseId): string | null {
  return moveById(id)?.hi ?? null;
}

/** A move's first-line cue in Hindi, or null where there is none yet. */
export function hiCue(id: ExerciseId): string | null {
  return CUE_HI[id] ?? null;
}

/** A filled cue ("hands allowed; one hand on the counter") phrase by phrase, each in Hindi where
 *  there is one. */
export function cueHi(cue: string): string {
  return cue
    .split("; ")
    .map((p) => tr("hi", p))
    .join("; ");
}
